import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import {
  BookOpen, Target, TrendingUp, PenTool, Calendar,
  Lock, ArrowRight, Award, Sparkles, ArrowLeft, CheckCircle, Scale,
  FileText, Brain, Lightbulb, ChevronRight, Clock, Flame,
  GraduationCap, Trophy, Star, Play, Zap, BarChart3,
  Landmark, MapPin, Building2, Users, Globe, Shield
} from "lucide-react";

/**
 * TGPSC Mains Hub - Answer Writing Preparation
 * Reuses UPSC Mains Hub structure with TGPSC-specific GS papers
 */

const TGPSCMainsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPro = user?.role === "PRO";
  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  // TGPSC Mains countdown - September 2026
  const MAINS_DATE = new Date('2026-09-15');
  const getDaysRemaining = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(MAINS_DATE);
    exam.setHours(0, 0, 0, 0);
    return Math.ceil((exam - today) / (1000 * 60 * 60 * 24));
  };
  const daysToExam = getDaysRemaining();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/mains/stats?exam_type=TGPSC`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // TGPSC GS Suites - Based on TGPSC Mains pattern
  const gsSuites = [
    { 
      title: "Paper-I (GS)", 
      subtitle: "History, Geography, Culture",
      icon: Landmark, 
      color: "from-pink-500 to-rose-600",
      path: "/tgpsc/mains/gs1",
      topics: ["Telangana History", "Indian History", "Geography", "Culture & Heritage"],
      questionsCompleted: stats?.gs1_completed || 0
    },
    { 
      title: "Paper-II (GS)", 
      subtitle: "Polity, Governance, IR",
      icon: Scale, 
      color: "from-indigo-500 to-blue-600",
      path: "/tgpsc/mains/gs2",
      topics: ["Indian Polity", "Governance", "Constitution", "International Relations"],
      questionsCompleted: stats?.gs2_completed || 0
    },
    { 
      title: "Paper-III (GS)", 
      subtitle: "Economy, Science, Environment",
      icon: Building2, 
      color: "from-emerald-500 to-teal-600",
      path: "/tgpsc/mains/gs3",
      topics: ["Indian Economy", "Telangana Economy", "Science & Tech", "Environment"],
      questionsCompleted: stats?.gs3_completed || 0
    },
    { 
      title: "Paper-IV (GS)", 
      subtitle: "Aptitude, Ethics",
      icon: Brain, 
      color: "from-purple-500 to-violet-600",
      path: "/tgpsc/mains/gs4",
      topics: ["Ethics", "Aptitude", "Decision Making", "Case Studies"],
      questionsCompleted: stats?.gs4_completed || 0
    },
    { 
      title: "Paper-V", 
      subtitle: "Telangana Movement & State Formation",
      icon: Users, 
      color: "from-orange-500 to-amber-600",
      path: "/tgpsc/mains/paper5",
      topics: ["Telangana Movement", "State Formation", "Key Events", "Leaders"],
      questionsCompleted: stats?.paper5_completed || 0
    },
    { 
      title: "Paper-VI", 
      subtitle: "Telangana Society, Economy & Culture",
      icon: Globe, 
      color: "from-cyan-500 to-blue-600",
      path: "/tgpsc/mains/paper6",
      topics: ["Society & Culture", "Economy", "Welfare Schemes", "Development"],
      questionsCompleted: stats?.paper6_completed || 0
    },
  ];

  // Additional Features
  const additionalFeatures = [
    { 
      title: "Essay Practice", 
      description: "Practice essay writing for TGPSC Mains", 
      icon: FileText, 
      path: "/tgpsc/mains/essay",
      badge: "250 marks"
    },
    { 
      title: "PYQ Analysis", 
      description: "Previous year Mains questions", 
      icon: Target, 
      path: "/tgpsc/mains/pyq",
      badge: "2015-2024"
    },
    { 
      title: "Answer Templates", 
      description: "Structured answer frameworks", 
      icon: BookOpen, 
      path: "/tgpsc/mains/templates",
      badge: "PRO"
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 pb-20">
      {/* Back Navigation */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/tgpsc")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          TGPSC Dashboard
        </Button>
      </div>

      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-6 pb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <PenTool className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                  TGPSC Mains Hub
                </h1>
                <p className="text-slate-600">Answer Writing & Evaluation</p>
              </div>
            </div>
            <p className="text-slate-500 mt-2">
              Welcome, <span className="text-indigo-600 font-medium">{firstName}</span>! 
              Master the art of answer writing for TGPSC Mains.
            </p>
          </div>
          
          {/* Exam Countdown */}
          <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold">{daysToExam}</div>
                  <div className="text-xs text-indigo-200">Days Left</div>
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium">TGPSC Mains 2026</div>
                  <div className="text-xs text-indigo-200">~{weeksToExam} weeks</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-indigo-600">{stats?.total_answers || 0}</div>
              <div className="text-sm text-slate-600">Answers Written</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-emerald-600">{stats?.avg_score || 0}/10</div>
              <div className="text-sm text-slate-600">Avg. Score</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{stats?.papers_covered || 0}/6</div>
              <div className="text-sm text-slate-600">Papers Covered</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">{stats?.streak || 0}</div>
              <div className="text-sm text-slate-600">Day Streak</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* GS Suites */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          GS Paper Suites
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gsSuites.map((suite) => (
            <Card
              key={suite.title}
              className="hover:shadow-lg transition-all cursor-pointer overflow-hidden"
              onClick={() => navigate(suite.path)}
            >
              <div className={`h-2 bg-gradient-to-r ${suite.color}`} />
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${suite.color} flex items-center justify-center`}>
                    <suite.icon className="w-6 h-6 text-white" />
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {suite.questionsCompleted} done
                  </Badge>
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{suite.title}</h3>
                <p className="text-sm text-slate-600 mb-3">{suite.subtitle}</p>
                <div className="flex flex-wrap gap-1">
                  {suite.topics.slice(0, 3).map((topic) => (
                    <Badge key={topic} variant="secondary" className="text-xs bg-slate-100">
                      {topic}
                    </Badge>
                  ))}
                  {suite.topics.length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-slate-100">
                      +{suite.topics.length - 3}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Additional Features */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          Additional Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {additionalFeatures.map((feature) => (
            <Card
              key={feature.title}
              className="hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate(feature.path)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {feature.badge}
                  </Badge>
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* TGPSC Mains Pattern Info */}
      <div className="max-w-5xl mx-auto px-4">
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              TGPSC Mains Pattern
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-indigo-800">Paper I-IV (GS)</p>
                <p className="text-indigo-600">150 marks each × 4 = 600 marks</p>
              </div>
              <div>
                <p className="font-medium text-indigo-800">Paper V-VI (Telangana)</p>
                <p className="text-indigo-600">150 marks each × 2 = 300 marks</p>
              </div>
              <div>
                <p className="font-medium text-indigo-800">Total Mains</p>
                <p className="text-indigo-600">900 marks (Descriptive)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TGPSCMainsHubPage;
